/**
 * TBMuktBharat Feed Page - Fixed Version
 * - Working file upload for photos/videos
 * - Cursor-based loading (max 500 posts)
 * - Live news feed
 * - Optimized performance
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import postService, { Post } from '@/services/postService'
import { getApiErrorMessage } from '@/services/api'
import newsService, { NewsItem } from '@/services/newsService'
import GovLayout from '@/components/layout/GovLayout'
import {
  Heart, MessageCircle, Share2, Bookmark, X, Image as ImageIcon, Video,
  Award, Calendar, ChevronRight, Activity, FileText, Home, Search,
  LogOut, Bell, Menu, BookOpen, Target, Settings, Loader2, Upload,
  ExternalLink, Newspaper, Repeat, Trash2, Maximize2, Flag
} from 'lucide-react'
import { HashtagText } from '@/lib/hashtagLinkify'
import Lightbox from '@/components/Lightbox'
import ReportModal from '@/components/common/ReportModal'


// ================================================================================
// POST CARD - Optimized with Comment & Share
// ================================================================================
// Exported so the post detail page (/post/:id) can render the EXACT same card
// as the feed — guarantees the detail view stays visually identical to the feed
// without duplicating this markup. Adding `export` does not change the in-file
// usage below; it only makes the component importable.
export const PostCard = React.memo(({ post, onReaction, onSave, onComment, onShare, onReshare, canDelete, onDelete, canReport, onReport, onTagClick }: {
  post: Post;
  onReaction?: (id: number) => void;
  onSave?: (id: number) => void;
  onComment?: (id: number) => void;
  onShare?: (id: number) => void;
  onReshare?: (id: number) => void;
  canDelete?: boolean;
  onDelete?: (id: number) => void;
  canReport?: boolean;
  onReport?: (id: number) => void;
  onTagClick?: (tag: string) => void;
}) => {
  const [isLiked, setIsLiked] = useState(post.isLiked || false)
  const [likes, setLikes] = useState(post.likeCount || 0)
  const [isSaved, setIsSaved] = useState(post.isSaved || false)
  const [isReshared, setIsReshared] = useState(post.isReshared || false)
  const [reshares, setReshares] = useState(post.reshareCount || 0)
  const [showShareMenu, setShowShareMenu] = useState(false)
  // Lightbox for viewing the full, uncropped media. Clicking the image (or the
  // expand button on a video) opens it; Esc/backdrop/X close it.
  const [showLightbox, setShowLightbox] = useState(false)

  const formatTime = useCallback((date: string) => {
    const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000)
    if (diff < 60) return 'Just now'
    if (diff < 3600) return `${Math.floor(diff / 60)}m`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`
    return new Date(date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
  }, [])

  const firstMedia = post.media?.[0]
  const hasMedia = firstMedia?.mediaUrl

  const handleShare = () => {
    const url = `${window.location.origin}/post/${post.id}`
    if (navigator.share) {
      navigator.share({
        title: 'Check out this post',
        text: post.content?.substring(0, 100) || 'TB Mukt Bharat Post',
        url: url
      }).catch(() => {
        navigator.clipboard.writeText(url)
        alert('Link copied to clipboard!')
      })
    } else {
      navigator.clipboard.writeText(url)
      alert('Link copied to clipboard!')
    }
    onShare?.(post.id)
    setShowShareMenu(false)
  }

  return (
    <article className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
      {hasMedia && (
        <div className="relative bg-gray-50 group">
          {firstMedia.mediaType === 'VIDEO' ? (
            <div className="aspect-video">
              <video src={firstMedia.mediaUrl} controls className="w-full h-full object-contain" />
            </div>
          ) : (
            <div className="max-h-96 overflow-hidden cursor-zoom-in" onClick={() => setShowLightbox(true)}>
              <img
                src={firstMedia.mediaUrl}
                alt=""
                className="w-full h-auto object-contain max-h-96"
                loading="lazy"
                style={{ aspectRatio: 'auto' }}
              />
            </div>
          )}
          {/* Expand button — opens the full media in a lightbox. For images the
              whole image is also clickable; for videos this button is the tap
              target so the inline video controls stay untouched. */}
          <button
            onClick={() => setShowLightbox(true)}
            title="View full"
            aria-label="View full media"
            className="absolute top-2 right-2 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <img src={post.user.profileImage || '/default-avatar.svg'} alt="" className="w-9 h-9 rounded-full object-cover" loading="lazy" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm truncate">{post.user.firstName} {post.user.lastName}</p>
            <p className="text-xs text-gray-500">{formatTime(post.createdAt)}</p>
          </div>
          <button onClick={() => { setIsSaved(!isSaved); onSave?.(post.id) }} className="p-1.5 hover:bg-gray-100 rounded-full">
            <Bookmark className={`w-4 h-4 ${isSaved ? 'fill-yellow-500 text-yellow-500' : 'text-gray-400'}`} />
          </button>
        </div>

        <div className="text-gray-800 text-sm mb-3 line-clamp-3">
          <HashtagText content={post.content || ''} onTagClick={(tag) => onTagClick?.(tag)} />
        </div>

        {post.hashtags && post.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {post.hashtags.map((tag) => (
              <button
                key={tag}
                onClick={() => onTagClick?.(tag)}
                className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full hover:bg-blue-100"
              >
                #{tag}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-3">
            <button onClick={() => { setIsLiked(!isLiked); setLikes(prev => isLiked ? prev - 1 : prev + 1); onReaction?.(post.id) }} className={`flex items-center gap-1 ${isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-500'}`}>
              <Heart className={`w-4 h-4 ${isLiked && 'fill-current'}`} />
              <span className="text-xs">{likes > 0 && likes}</span>
            </button>
            <button onClick={() => onComment?.(post.id)} className="flex items-center gap-1 text-gray-400 hover:text-blue-500">
              <MessageCircle className="w-4 h-4" />
              <span className="text-xs">{post.commentCount > 0 && post.commentCount}</span>
            </button>
            <button
              onClick={() => {
                const next = !isReshared
                setIsReshared(next)
                setReshares(prev => next ? prev + 1 : Math.max(0, prev - 1))
                onReshare?.(post.id)
              }}
              className={`flex items-center gap-1 ${isReshared ? 'text-green-600' : 'text-gray-400 hover:text-green-500'}`}
              title={isReshared ? 'Remove reshare' : 'Reshare'}
            >
              <Repeat className={`w-4 h-4 ${isReshared && 'fill-current'}`} />
              <span className="text-xs">{reshares > 0 && reshares}</span>
            </button>
            <button onClick={handleShare} className="text-gray-400 hover:text-green-500"><Share2 className="w-4 h-4" /></button>
          </div>
          {canDelete && (
            <button
              onClick={() => onDelete?.(post.id)}
              className="p-1.5 hover:bg-red-50 rounded-full text-gray-400 hover:text-red-500"
              title="Delete post"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {canReport && (
            <button
              onClick={() => onReport?.(post.id)}
              className="p-1.5 hover:bg-red-50 rounded-full text-gray-400 hover:text-red-500"
              title="Report post"
            >
              <Flag className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Full-media lightbox — fixed, so it renders above everything regardless
          of the card's overflow-hidden. */}
      {showLightbox && hasMedia && (
        <Lightbox
          src={firstMedia.mediaUrl}
          mediaType={firstMedia.mediaType}
          onClose={() => setShowLightbox(false)}
        />
      )}
    </article>
  )
})

// ================================================================================
// CREATE POST WIDGET - WITH FILE UPLOAD
// ================================================================================
const CreatePostWidget = React.memo(({ user, onPost }: { user: any; onPost: (content: string, mediaIds: number[], hashtags: string[]) => Promise<void> }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(false)
  const [selectedMedia, setSelectedMedia] = useState<File | null>(null)
  const [mediaPreview, setMediaPreview] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [hashtags, setHashtags] = useState<string[]>([])
  const [hashtagInput, setHashtagInput] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const addHashtag = useCallback(() => {
    const raw = hashtagInput.trim()
    if (!raw) return
    // Strip leading '#' and lowercase, dedup.
    const tag = (raw.startsWith('#') ? raw.slice(1) : raw).toLowerCase()
    if (tag && !hashtags.includes(tag)) {
      setHashtags(prev => [...prev, tag])
    }
    setHashtagInput('')
  }, [hashtagInput, hashtags])

  const removeHashtag = useCallback((tag: string) => {
    setHashtags(prev => prev.filter(t => t !== tag))
  }, [])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 20 * 1024 * 1024) {
        const mb = (file.size / (1024 * 1024)).toFixed(1)
        alert(`This file is ${mb} MB. The maximum allowed upload is 20 MB. Please choose a smaller file.`)
        return
      }
      setSelectedMedia(file)
      const reader = new FileReader()
      reader.onloadend = () => setMediaPreview(reader.result as string)
      reader.readAsDataURL(file)
    }
  }, [])

  const handlePost = async () => {
    if (!content.trim() && !selectedMedia) return
    setLoading(true)
    try {
      let mediaIds: number[] = []
      if (selectedMedia) {
        setUploading(true)
        const uploadRes = await postService.uploadMedia(selectedMedia)
        if (uploadRes.success && uploadRes.data?.id) {
          mediaIds = [uploadRes.data.id]
        }
        setUploading(false)
      }
      await onPost(content, mediaIds, hashtags)
      setContent('')
      setSelectedMedia(null)
      setMediaPreview(null)
      setHashtags([])
      setHashtagInput('')
      setIsOpen(false)
    } catch (e) {
      console.error(e)
      alert(getApiErrorMessage(e, 'Failed to post. Please try again.'))
    }
    setLoading(false)
  }

  const removeMedia = useCallback(() => {
    setSelectedMedia(null)
    setMediaPreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  return (
    <>
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-3">
          <img src={user?.profileImage || '/default-avatar.svg'} alt="" className="w-10 h-10 rounded-full object-cover" />
          <button onClick={() => setIsOpen(true)} className="flex-1 text-left px-4 py-2.5 bg-gray-50 hover:bg-gray-100 rounded-lg text-gray-500 text-sm border border-gray-200">
            Share your TB journey...
          </button>
        </div>
        <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
          <button onClick={() => setIsOpen(true)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg font-medium text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors">
            <ImageIcon className="w-4 h-4" /> Photo
          </button>
          <button onClick={() => setIsOpen(true)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg font-medium text-sm text-red-600 bg-red-50 hover:bg-red-100 transition-colors">
            <Video className="w-4 h-4" /> Video
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setIsOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-xl">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold">Create Post</h2>
              <button onClick={() => setIsOpen(false)} className="p-1.5 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-4">
              <div className="flex gap-3 mb-3">
                <img src={user?.profileImage || '/default-avatar.svg'} alt="" className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <p className="font-semibold text-sm">{user?.firstName} {user?.lastName}</p>
                  <select className="text-xs bg-gray-100 rounded px-2 py-0.5 mt-0.5">
                    <option>🌍 Public</option>
                  </select>
                </div>
              </div>

              <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Share your TB journey..." className="w-full h-32 resize-none border-0 focus:outline-none text-sm" autoFocus />

              {/* Hashtag input — chips. Inline #tags in the caption are also
                  parsed server-side; these chips let authors add tags explicitly. */}
              <div className="mt-2">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {hashtags.map((tag) => (
                    <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full">
                      #{tag}
                      <button onClick={() => removeHashtag(tag)} className="hover:text-blue-800"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={hashtagInput}
                    onChange={(e) => setHashtagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault()
                        addHashtag()
                      }
                    }}
                    placeholder="Add hashtag (press Enter)"
                    className="flex-1 px-3 py-1.5 bg-gray-50 rounded-lg text-sm border border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <button type="button" onClick={addHashtag} className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100">Add</button>
                </div>
              </div>

              {mediaPreview && (
                <div className="relative mt-3">
                  {selectedMedia?.type.startsWith('video') ? (
                    <video src={mediaPreview} controls className="w-full max-h-64 rounded-lg object-cover" />
                  ) : (
                    <img src={mediaPreview} alt="" className="w-full max-h-64 rounded-lg object-cover" />
                  )}
                  <button onClick={removeMedia} className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-black/70 rounded-full text-white">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            <div className="px-4 pb-3">
              <input ref={fileInputRef} type="file" accept="image/*,video/*" className="hidden" onChange={handleFileSelect} />
              <button onClick={() => fileInputRef.current?.click()} disabled={!!selectedMedia} className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium disabled:opacity-50">
                <Upload className="w-4 h-4" />
                {selectedMedia ? 'Media selected' : 'Add Photo/Video'}
              </button>
            </div>

            <div className="p-4 border-t border-gray-100">
              <button onClick={handlePost} disabled={(!content.trim() && !selectedMedia) || loading} className="w-full py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {loading ? <>{uploading ? 'Uploading...' : 'Posting...'}<Loader2 className="w-4 h-4 animate-spin" /></> : 'Post'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
})

// ================================================================================
// MAIN FEED PAGE - WITH CURSOR LOADING
// ================================================================================
const PremiumFeedPage = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated, logout } = useAuthStore()
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [cursor, setCursor] = useState<number | null>(null)
  const [commentPostId, setCommentPostId] = useState<number | null>(null)
  const [commentText, setCommentText] = useState('')
  const [comments, setComments] = useState<any[]>([])
  const [loadingComments, setLoadingComments] = useState(false)
  const [submittingComment, setSubmittingComment] = useState(false)
  const [activeTab, setActiveTab] = useState(0) // 0: All Posts, 1: Success Stories, 2: Questions, 3: News
  const [news, setNews] = useState<NewsItem[]>([])
  const [loadingNews, setLoadingNews] = useState(false)
  const [reportPostId, setReportPostId] = useState<number | null>(null)
  const PAGE_SIZE = 15
  const MAX_POSTS = 500
  const observerRef = useRef<IntersectionObserver | null>(null)
  const lastPostRef = useRef<HTMLDivElement | null>(null)

  // Load news when News tab is selected
  useEffect(() => {
    if (activeTab === 3 && news.length === 0) {
      setLoadingNews(true)
      newsService.getNews(0, 10).then(data => {
        setNews(data)
        setLoadingNews(false)
      }).catch(() => setLoadingNews(false))
    }
  }, [activeTab, news.length])

  // Filter posts based on active tab
  const filteredPosts = useMemo(() => {
    if (activeTab === 0) return posts // All Posts

    switch (activeTab) {
      case 1: // Success Stories - posts with #success or "success" keyword
        return posts.filter(post => {
          const text = (post.content || '').toLowerCase()
          const tags = (post as any).hashtags || []
          return text.includes('success') || text.includes('#success') ||
                 text.includes('recovered') || text.includes('cured') ||
                 text.includes('survivor') || text.includes('story') ||
                 tags.some((t: string) => t.toLowerCase().includes('success') || t.toLowerCase().includes('story'))
        })
      case 2: // Questions - posts with ? or #question
        return posts.filter(post => {
          const text = (post.content || '').toLowerCase()
          const tags = (post as any).hashtags || []
          return text.includes('?') || text.includes('#question') ||
                 text.includes('#help') || text.includes('how do') ||
                 text.includes('what is') || text.includes('why') ||
                 text.includes('can i') || text.includes('should i') ||
                 tags.some((t: string) => t.toLowerCase().includes('question') || t.toLowerCase().includes('help'))
        })
      case 3: // News - posts with #news or news-related keywords
        return posts.filter(post => {
          const text = (post.content || '').toLowerCase()
          const tags = (post as any).hashtags || []
          return text.includes('#news') || text.includes('news') ||
                 text.includes('update') || text.includes('announcement') ||
                 text.includes('campaign') || text.includes('event') ||
                 text.includes('government') || text.includes('ministry') ||
                 tags.some((t: string) => t.toLowerCase().includes('news') || t.toLowerCase().includes('update'))
        })
      default:
        return posts
    }
  }, [posts, activeTab])

  // FAST cursor-based loading - uses WHERE id < cursor instead of OFFSET
  const loadPosts = useCallback(async (reset = false) => {
    if (!reset && (loadingMore || !hasMore)) return

    if (reset) {
      setLoading(true)
      setCursor(null)
    } else {
      setLoadingMore(true)
    }

    try {
      // Use cursor-based API for MUCH faster loading
      const currentCursor = reset ? undefined : cursor || undefined
      const res = await postService.getFeedPostsCursor(currentCursor, PAGE_SIZE)
      const newPosts = res.data || []

      if (newPosts.length > 0) {
        if (reset) {
          setPosts(newPosts)
        } else {
          setPosts(prev => {
            const combined = [...prev, ...newPosts]
            return combined.slice(0, MAX_POSTS)
          })
        }
        // Set cursor to last post ID for next fetch
        setCursor(newPosts[newPosts.length - 1].id)
        setHasMore(newPosts.length === PAGE_SIZE)
      } else {
        setHasMore(false)
      }
    } catch (e) {
      console.error(e)
      setHasMore(false)
    }

    setLoading(false)
    setLoadingMore(false)
  }, [cursor, loadingMore, hasMore])

  useEffect(() => {
    if (isAuthenticated) loadPosts(true)
  }, [isAuthenticated])

  // Infinite scroll with Intersection Observer
  useEffect(() => {
    if (loading || !hasMore) return

    const handleObserver = (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore) {
        loadPosts(false)
      }
    }

    observerRef.current = new IntersectionObserver(handleObserver, { threshold: 0.1 })
    if (lastPostRef.current) observerRef.current.observe(lastPostRef.current)

    return () => observerRef.current?.disconnect()
  }, [loading, hasMore, loadingMore, loadPosts])

  const handleCreatePost = useCallback(async (content: string, mediaIds: number[], hashtags: string[]) => {
    const res = await postService.createPost({
      content,
      postType: mediaIds.length > 0 ? 'IMAGE' : 'TEXT',
      mediaIds,
      hashtags: hashtags.length > 0 ? hashtags : undefined
    })
    if (res.success && res.data) {
      setPosts(prev => [res.data, ...prev].slice(0, MAX_POSTS))
    }
  }, [])

  const handleReaction = useCallback(async (postId: number) => {
    try {
      await postService.react(postId, 'LIKE')
    } catch (e) {
      console.error(e)
    }
  }, [])

  const handleSave = useCallback(async (postId: number) => {
    try {
      await postService.toggleSave(postId)
    } catch (e) {
      console.error(e)
    }
  }, [])

  const handleOpenComments = useCallback(async (postId: number) => {
    setCommentPostId(postId)
    setLoadingComments(true)
    setComments([])
    try {
      const res = await postService.getComments(postId, 0, 20)
      setComments(res.data?.content || res.data || [])
    } catch (e) {
      console.error(e)
    }
    setLoadingComments(false)
  }, [])

  const handleSubmitComment = useCallback(async () => {
    if (!commentPostId || !commentText.trim()) return
    setSubmittingComment(true)
    try {
      await postService.addComment(commentPostId, commentText.trim())
      setCommentText('')
      // Reload comments
      const res = await postService.getComments(commentPostId, 0, 20)
      setComments(res.data?.content || res.data || [])
      // Update comment count in posts
      setPosts(prev => prev.map(p =>
        p.id === commentPostId ? { ...p, commentCount: p.commentCount + 1 } : p
      ))
    } catch (e) {
      console.error(e)
    }
    setSubmittingComment(false)
  }, [commentPostId, commentText])

  const handleShare = useCallback(async (postId: number) => {
    try {
      await postService.sharePost(postId)
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, shareCount: p.shareCount + 1 } : p
      ))
    } catch (e) {
      console.error(e)
    }
  }, [])

  // Reshare toggle — optimistic UI in the card; reconcile from the server
  // response (which carries the authoritative reshareCount + isReshared).
  const handleReshare = useCallback(async (postId: number) => {
    const post = posts.find(p => p.id === postId)
    const wasReshared = post?.isReshared || false
    try {
      const res = wasReshared
        ? await postService.unresharePost(postId)
        : await postService.resharePost(postId)
      const updated = res?.data
      setPosts(prev => prev.map(p =>
        p.id === postId
          ? {
              ...p,
              isReshared: updated?.isReshared ?? !wasReshared,
              reshareCount: updated?.reshareCount ?? p.reshareCount
            }
          : p
      ))
    } catch (e) {
      console.error(e)
    }
  }, [posts])

  const handleDelete = useCallback(async (postId: number) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return
    try {
      await postService.deletePost(postId)
      setPosts(prev => prev.filter(p => p.id !== postId))
    } catch (e) {
      console.error(e)
      alert('Failed to delete post.')
    }
  }, [])

  const handleTagClick = useCallback((tag: string) => {
    navigate(`/hashtag/${encodeURIComponent(tag.toLowerCase())}`)
  }, [navigate])

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm">
          <div className="text-5xl mb-4">🔒</div>
          <h2 className="text-xl font-bold mb-2">Login Required</h2>
          <p className="text-gray-500 mb-4 text-sm">Please login to view your feed</p>
          <button onClick={() => navigate('/login')} className="w-full py-2.5 bg-blue-600 text-white font-medium rounded-lg">Login Now</button>
        </div>
      </div>
    )
  }

  return (
    <GovLayout>
      <div className="max-w-xl mx-auto space-y-4">
            <CreatePostWidget user={user} onPost={handleCreatePost} />

            <div className="flex gap-2 overflow-x-auto pb-1">
              {['All Posts', 'Success Stories', 'Questions', 'News'].map((tab, i) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(i)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activeTab === i ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-4">
              {activeTab === 3 ? (
                // News Tab - Show live news
                loadingNews ? (
                  [...Array(4)].map((_, i) => (
                    <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                      <div className="h-32 bg-gray-200 rounded-lg mb-3" />
                      <div className="h-3 bg-gray-200 rounded w-3/4 mb-2" />
                      <div className="h-2 bg-gray-200 rounded w-1/2" />
                    </div>
                  ))
                ) : news.length === 0 ? (
                  <div className="bg-white rounded-xl p-8 text-center">
                    <div className="text-4xl mb-3">📰</div>
                    <h3 className="text-lg font-bold mb-1">No news available</h3>
                    <p className="text-gray-500 text-sm">Check back later for TB-related news updates</p>
                  </div>
                ) : (
                  news.map((item) => (
                    <article key={item.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                      <div className="h-40 bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center">
                        <svg className="w-16 h-16 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                        </svg>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full">{item.category}</span>
                          <span className="text-xs text-gray-400">{item.publishedAt ? new Date(item.publishedAt).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }) : ''}</span>
                        </div>
                        <h3 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2">{item.title}</h3>
                        <p className="text-gray-600 text-xs line-clamp-3 mb-3">{item.summary}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">{item.source}</span>
                          <a href={item.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs font-medium">
                            Read more <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                          </a>
                        </div>
                      </div>
                    </article>
                  ))
                )
              ) : loading ? (
                [...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                    <div className="h-32 bg-gray-200 rounded-lg mb-3" />
                    <div className="h-3 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-2 bg-gray-200 rounded w-1/2" />
                  </div>
                ))
              ) : filteredPosts.length === 0 ? (
                <div className="bg-white rounded-xl p-8 text-center">
                  <div className="text-4xl mb-3">📰</div>
                  <h3 className="text-lg font-bold mb-1">{activeTab === 0 ? 'No posts yet' : `No ${['All Posts', 'Success Stories', 'Questions', 'News'][activeTab]} found`}</h3>
                  <p className="text-gray-500 text-sm">{activeTab === 0 ? 'Be the first to share!' : 'Try another tab or create a new post'}</p>
                </div>
              ) : (
                <>
                  {filteredPosts.map((post) => <PostCard key={post.id} post={post} onReaction={handleReaction} onSave={handleSave} onComment={handleOpenComments} onShare={handleShare} onReshare={handleReshare} canDelete={!!user && post.user.id === user.id} onDelete={handleDelete} canReport={!!user && post.user.id !== user.id} onReport={setReportPostId} onTagClick={handleTagClick} />)}
                  {hasMore && <div ref={lastPostRef} className="flex justify-center py-4">{loadingMore && <Loader2 className="w-6 h-6 animate-spin text-blue-600" />}</div>}
                </>
              )}
            </div>
      </div>

      {/* Comment Modal */}
      {commentPostId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setCommentPostId(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold">Comments</h2>
              <button onClick={() => setCommentPostId(null)} className="p-1.5 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              {loadingComments ? (
                <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600" /></div>
              ) : comments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No comments yet</p>
                  <p className="text-sm">Be the first to comment!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {comments.map((comment: any) => (
                    <div key={comment.id} className="flex gap-3">
                      <img src={comment.user?.profileImage || '/default-avatar.svg'} alt="" className="w-8 h-8 rounded-full object-cover" />
                      <div className="flex-1">
                        <div className="bg-gray-100 rounded-lg px-3 py-2">
                          <p className="font-medium text-sm">{comment.user?.firstName} {comment.user?.lastName}</p>
                          <p className="text-sm text-gray-700">{comment.content}</p>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                          <span>{new Date(comment.createdAt).toLocaleDateString()}</span>
                          <button className="hover:text-blue-600">Like</button>
                          <button className="hover:text-blue-600">Reply</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-100 flex gap-3">
              <img src={user?.profileImage || '/default-avatar.svg'} alt="" className="w-8 h-8 rounded-full object-cover" />
              <div className="flex-1 flex gap-2">
                <input
                  type="text"
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  placeholder="Write a comment..."
                  className="flex-1 px-4 py-2 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyDown={e => e.key === 'Enter' && handleSubmitComment()}
                />
                <button
                  onClick={handleSubmitComment}
                  disabled={!commentText.trim() || submittingComment}
                  className="px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-medium disabled:opacity-50"
                >
                  {submittingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* BottomNav removed — GovLayout provides the mobile bottom nav */}

      {/* Report post modal — shown when a viewer flags someone else's post */}
      {reportPostId && (
        <ReportModal postId={reportPostId} onClose={() => setReportPostId(null)} />
      )}
    </GovLayout>
  )
}

export default PremiumFeedPage